import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Music2 } from "lucide-react";

const GENRES = [
  { match: /ocho de r[ií]o|8 de r[ií]o/i, en: "Metal & alternative", es: "Metal y alternativa" },
  { match: /^el boricua$/i, en: "Bomba & live drums", es: "Bomba y percusión" },
  { match: /ensayo/i, en: "Live music, theater & bar", es: "Música en vivo, teatro y bar" },
  { match: /club 77/i, en: "Punk & indie", es: "Punk e indie" },
  { match: /caf[eé] borik[eé]n/i, en: "Bomba · Karaoke · Billiards", es: "Bomba · Karaoke · Billar" },
];

function VenuePhoto({ venue, es }) {
  const image = venue.image || venue.photos?.[0];
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image]);

  return (
    <span className="relative block aspect-square w-[94px] shrink-0 overflow-hidden bg-[#262522] sm:w-[112px] lg:w-[124px]">
      {image && !failed ? (
        <img
          src={image}
          alt={venue.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.035]"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[#292724] px-2 text-center text-white/80">
          <Music2 className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
          <span className="text-[9px] font-semibold uppercase leading-[1.35] tracking-[.08em]">
            {es ? "Foto pendiente" : "Photo coming soon"}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * Río Piedras' living-music editorial index.
 * No bordered business tiles, drop shadows or competing actions: a strong
 * photograph, numbered venue name, music note and a single tap target.
 */
export default function RioPiedrasLiveMusic({ venues = [], es = false }) {
  const lineup = venues.slice(0, 5);
  if (lineup.length < 2) return null;

  return (
    <section
      className="mx-4 mt-5 sm:mx-6 sm:mt-7 lg:mx-auto lg:max-w-[1180px]"
      aria-label={es ? "La escena musical de Río Piedras" : "Río Piedras music scene"}
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-[#bcb9b1] pb-2 dark:border-white/30">
        <p className="text-[10px] font-extrabold uppercase tracking-[.15em] text-[#b64236] dark:text-[#f4a29a]">
          {es ? "LA ESCENA / LUGARES" : "THE SCENE / VENUES"}
        </p>
        <span className="font-mono text-[10px] tabular-nums tracking-[.08em] text-[#8a867d] dark:text-white/55">
          {String(lineup.length).padStart(2, "0")} {es ? "ESPACIOS" : "PLACES"}
        </span>
      </div>

      <ul className="grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:gap-x-12">
        {lineup.map((venue, index) => {
          const genre = GENRES.find((item) => item.match.test((venue.title || "").trim()));
          return (
            <li key={venue.id} className="min-w-0 border-b border-[#dedbd5] dark:border-white/15">
              <Link
                to={venue.path || "/RioPiedras"}
                aria-label={`${venue.title}${genre ? `, ${es ? genre.es : genre.en}` : ""}`}
                className="group flex min-w-0 items-center gap-3 py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b64236] sm:gap-4 sm:py-4"
              >
                <VenuePhoto venue={venue} es={es} />

                <span className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
                  <span className="font-mono text-[9px] font-semibold tabular-nums tracking-[.12em] text-[#b64236] dark:text-[#f4a29a]">
                    {String(index + 1).padStart(2, "0")} / RÍO PIEDRAS
                  </span>
                  <span className="line-clamp-2 text-[17px] font-extrabold leading-[1.12] tracking-[-.035em] text-[#1b1b19] transition-colors group-hover:text-[#a33832] dark:text-[#f6f3ee] dark:group-hover:text-[#ffa59b] sm:text-[19px]">
                    {(venue.title || "").trim()}
                  </span>
                  {genre && (
                    <span className="text-[10px] font-medium uppercase leading-[1.4] tracking-[.075em] text-[#7d7972] dark:text-white/60">
                      {es ? genre.es : genre.en}
                    </span>
                  )}
                </span>

                <ArrowUpRight
                  className="h-[17px] w-[17px] shrink-0 self-center text-[#8a867e] transition-transform group-hover:translate-x-[2px] group-hover:-translate-y-[2px] group-hover:text-[#b64236] dark:text-white/55"
                  strokeWidth={1.7}
                  aria-hidden="true"
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}